import { IClauseChecklistList, readClauseChecklists } from "@/api";

import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { useFilterModel } from "@/hooks/useFilterModel";
import { Grid, Stack, TablePagination } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { ClauseChecklistCard } from "./ClauseChecklistCard";
import { ClauseChecklistSkeleton } from "./ClauseChecklistSkeleton";
import { ClauseChecklistsListHeader } from "./ClauseChecklistsListHeader";
import { ClauseChecklistsListToolbar } from "./ClauseChecklistsListToolbar";
import { EmptyClauseChecklistsState } from "./EmptyClauseChecklistsState";

/**
 * Displays a list of clause checklists.
 */
export const ClauseChecklistsList = () => {
    const { organization } = useOrganization();
    const [clauseChecklists, setClauseChecklists] = useState<
        IClauseChecklistList[]
    >([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const { filterModel, handleFilterModelChange } = useFilterModel();

    const { data: checklistsData, isPending: isChecklistsPending } = useQuery({
        queryKey: [
            "organization-clause-checklists",
            organization.id,
            page,
            pageSize,
            filterModel,
        ],
        queryFn: () => readClauseChecklists(page + 1, pageSize, filterModel),
    });

    useEffect(() => {
        if (checklistsData) {
            setClauseChecklists(checklistsData.items);
        }
    }, [checklistsData]);

    // memoize rowCount to avoid resetting the page to 0 when the data is loading
    const rowCountRef = useRef(checklistsData?.total || 0);

    const rowCount = useMemo(() => {
        if (checklistsData?.total !== undefined) {
            rowCountRef.current = checklistsData?.total;
        }
        return rowCountRef.current;
    }, [checklistsData?.total]);

    const isPending = isChecklistsPending;

    return (
        <div>
            <ClauseChecklistsListHeader />
            <ClauseChecklistsListToolbar
                handleFilterModelChange={handleFilterModelChange}
            />
            <Stack pt={0.5}>
                <Stack>
                    <Grid container spacing={2}>
                        {isPending &&
                            [...Array(pageSize)].map((_, index) => (
                                <Grid
                                    key={index}
                                    size={{ xs: 12, md: 6, xl: 4 }}
                                >
                                    <ClauseChecklistSkeleton />
                                </Grid>
                            ))}
                        {!isPending &&
                            clauseChecklists.length > 0 &&
                            clauseChecklists.map((clauseChecklist) => (
                                <Grid
                                    key={clauseChecklist.id}
                                    size={{ xs: 12, md: 6, xl: 4 }}
                                >
                                    <ClauseChecklistCard
                                        clauseChecklist={clauseChecklist}
                                    />
                                </Grid>
                            ))}
                    </Grid>

                    {!isPending && clauseChecklists.length === 0 && (
                        <EmptyClauseChecklistsState />
                    )}
                    {!isPending && rowCount > pageSize && (
                        <TablePagination
                            component="div"
                            count={rowCount}
                            page={page}
                            rowsPerPage={pageSize}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) =>
                                setPageSize(Number(event.target.value))
                            }
                            sx={{
                                "& .MuiTablePagination-spacer": {
                                    display: "none",
                                },
                            }}
                        />
                    )}
                </Stack>
            </Stack>
        </div>
    );
};
